import { ReactNode, useMemo, useRef } from "react";
import { ApolloClient, ApolloProvider } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import buildCache from "./cache";
import { getImpersonationFailure } from "./errors";
import { getImpersonationHeaders } from "./headers";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { getActiveBackendSource, getGraphqlUri } from "@/lib/backendSource";
import { createCommissionMockLink } from "@/pages/billing/finance/commission/_mocks/commissionMockLink";

export default function GraphqlProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useCurrentUser();
  const { assumedUser, reportImpersonationError, clearAssumedUserSession } = useAssumeUser();
  const assumedUserId = assumedUser?.id;
  const assumedUserIdRef = useRef(assumedUserId);
  assumedUserIdRef.current = assumedUserId;
  const backendSource = getActiveBackendSource();

  const client = useMemo(() => {
    const uri = getGraphqlUri();
    const httpWithUploadLink = createUploadLink({
      uri: (operation) => `${uri}?op=${operation.operationName}`
    });
    const authLink = setContext((_, { headers }) =>
      getAccessToken().then((token) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
          ...getImpersonationHeaders(assumedUserIdRef.current),
          ...(import.meta.env.VITE_APP_VERSION != null
            ? {
                "X-Client-Version": import.meta.env.VITE_APP_VERSION,
                "X-Client-Name": window.location.hostname
              }
            : {})
        }
      }))
    );

    const assumedUserErrorLink = onError(({ graphQLErrors }) => {
      const impersonationFailure = getImpersonationFailure(graphQLErrors);
      const activeAssumedUserId = assumedUserIdRef.current;
      if (activeAssumedUserId && impersonationFailure) {
        reportImpersonationError(impersonationFailure, activeAssumedUserId);
        clearAssumedUserSession(activeAssumedUserId);
      }
    });

    const terminalLink = authLink.concat(assumedUserErrorLink).concat(httpWithUploadLink);
    const useCommissionMocks = import.meta.env.DEV && import.meta.env.VITE_COMMISSION_GRAPHQL_MOCKS === "true";
    const link = useCommissionMocks ? createCommissionMockLink().concat(terminalLink) : terminalLink;
    return new ApolloClient({
      cache: buildCache(),
      link,
      defaultOptions: { watchQuery: { fetchPolicy: "cache-and-network" } }
    });
    // Impersonation headers read assumedUserIdRef; assume start resets the cache on the assume-user page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendSource, reportImpersonationError, clearAssumedUserSession]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
